import type { BackgroundDto, BrandDto, CharacterDto, GpuDto, KnowledgeDocDto, ProductDto, ProjectDto } from '@studio/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export const useCharacters = () =>
  useQuery({
    queryKey: ['characters'],
    queryFn: () => api<CharacterDto[]>('/api/characters'),
    // A cast picture is being made on the GPU.
    refetchInterval: (q) =>
      q.state.data?.some((c) => c.castJob && ['queued', 'uploading', 'submitted', 'running', 'downloading'].includes(c.castJob.status))
        ? 3000
        : false,
  });
export const useBackgrounds = () =>
  useQuery({ queryKey: ['backgrounds'], queryFn: () => api<BackgroundDto[]>('/api/backgrounds') });
export const useProducts = () =>
  useQuery({ queryKey: ['products'], queryFn: () => api<ProductDto[]>('/api/products') });
export const useBrand = () => useQuery({ queryKey: ['brand'], queryFn: () => api<BrandDto>('/api/brand') });
export const useKnowledge = () =>
  useQuery({ queryKey: ['knowledge'], queryFn: () => api<KnowledgeDocDto[]>('/api/knowledge') });

/** The GPU card: refreshed every few seconds while it is doing something. */
export const useGpu = () =>
  useQuery({
    queryKey: ['gpu'],
    queryFn: () => api<GpuDto>('/api/gpu'),
    refetchInterval: (q) => {
      const d = q.state.data;
      return d && (d.status !== 'off' || d.queue.queued + d.queue.running + d.queue.waiting > 0) ? 3000 : 15_000;
    },
  });

const ACTIVE = new Set(['queued', 'uploading', 'submitted', 'running', 'downloading']);
export const projectBusy = (p: ProjectDto | undefined) =>
  !!p &&
  (p.finishStatus === 'assembling' ||
    p.shots.some((s) => s.takes.some((t) => ACTIVE.has(t.status)) || (!!s.frameJob && ACTIVE.has(s.frameJob.status))) ||
    p.cast.some((c) => !!c.castJob && ACTIVE.has(c.castJob.status)));
export const isActive = (status: string) => ACTIVE.has(status);

export const useProject = (id: string) =>
  useQuery({
    queryKey: ['project', id],
    queryFn: () => api<ProjectDto>(`/api/projects/${id}`),
    refetchInterval: (q) => (projectBusy(q.state.data) ? 2000 : false),
  });
